import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import PublicLayout from "../../components/layout/PublicLayout";
import heroImage from "../../assets/images/unzippd-logo.png";
import {
  IN_THE_NEWS_FILTER_TAGS,
  IN_THE_NEWS_ITEMS,
} from "../../constants/inTheNews";

const linkAccentClass = "text-[#77A8FF]";

const cardBorder =
  "rounded-2xl border border-[#404040] bg-[#202020] box-border overflow-hidden";

const fullReleaseCtaClass =
  "inline-flex items-center gap-2 font-semibold no-underline text-[rgb(77_148_255)] hover:text-[rgb(130_178_255)] underline-offset-2 hover:underline hover:gap-3 transition-[gap,color]";

const SECONDARY_CARD_IMAGE_ASPECT_CLASS =
  "relative w-full aspect-[340/204] shrink-0 overflow-hidden bg-[#111]";

const LG_BREAKPOINT_PX = 1024;

const FeaturedCard = ({ item }) => {
  const imageWrapRef = useRef(null);
  const [contentBoxPx, setContentBoxPx] = useState(undefined);

  useLayoutEffect(() => {
    const el = imageWrapRef.current;

    if (!el || typeof window === "undefined") return;

    const sync = () => {
      if (window.innerWidth < LG_BREAKPOINT_PX) {
        setContentBoxPx(undefined);
        return;
      }

      setContentBoxPx(el.offsetHeight);
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    window.addEventListener("resize", sync);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [item?.slug, item?.image]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-0 pb-6 sm:pb-8 hidden md:block">
      <article
        className={`mx-auto max-w-7xl min-w-0 flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-10 ${cardBorder}`}
      >
        <div
          ref={imageWrapRef}
          className="relative w-full aspect-[340/204] shrink-0 overflow-hidden bg-[#111] lg:w-[45%] xl:w-[42%] lg:self-start"
        >
          <img
            src={item.image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        </div>

        <div
          className="box-border flex min-w-0 w-full flex-1 flex-col gap-4 p-6 sm:p-8 lg:p-5 lg:pl-4 lg:min-h-0 lg:overflow-y-auto lg:justify-between"
          style={
            contentBoxPx != null
              ? { minHeight: contentBoxPx, maxHeight: contentBoxPx }
              : undefined
          }
        >
          <p className="m-0 text-xs sm:text-sm text-gray-400">{item.date}</p>

          <h2 className="m-0 text-lg sm:text-xl md:text-2xl font-bold text-white leading-tight">
            {item.title}
          </h2>

          <p className="m-0 text-sm text-gray-300 leading-relaxed">
            {item.summary}
          </p>

          {item.alsoCoveredBy && item.alsoCoveredBy.length > 0 ? (
            <p className="m-0 text-xs sm:text-sm text-gray-400">
              <span className="text-gray-500">Also covered by: </span>
              {item.alsoCoveredBy.map((c, i) => (
                <React.Fragment key={`${c.href}-${i}`}>
                  {i > 0 ? ", " : ""}
                  <a
                    href={c.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${linkAccentClass} underline-offset-2 hover:underline`}
                  >
                    {c.label}
                  </a>
                </React.Fragment>
              ))}
            </p>
          ) : null}

          {item.fullReleaseUrl ? (
            <div className="mt-2">
              <a
                href={item.fullReleaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`${fullReleaseCtaClass} text-sm`}
              >
                Read more
                <ArrowRight
                  className="h-4 w-4 shrink-0 text-current"
                  aria-hidden
                />
              </a>
            </div>
          ) : null}
        </div>
      </article>
    </div>
  );
};

const SecondaryCard = ({ item }) => (
  <article className={`w-full min-w-0 h-full flex flex-col ${cardBorder}`}>
    <div className={SECONDARY_CARD_IMAGE_ASPECT_CLASS}>
      <img
        src={item.image}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
    </div>
    <div className="flex flex-col gap-3 p-6 flex-1 min-h-0">
      <p className="m-0 text-[11px] sm:text-xs text-gray-400">{item.date}</p>
      <h3 className="m-0 text-base sm:text-lg font-semibold text-white leading-snug">
        {item.title}
      </h3>
      <p className="m-0 text-xs sm:text-sm text-gray-300 leading-relaxed">
        {item.summary}
      </p>
      {item.alsoCoveredBy && item.alsoCoveredBy.length > 0 ? (
        <p className="m-0 text-xs text-gray-400">
          <span className="text-gray-500">Also covered by: </span>
          {item.alsoCoveredBy.map((c, i) => (
            <React.Fragment key={`${c.href}-${i}`}>
              {i > 0 ? ", " : ""}
              <a
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkAccentClass} underline-offset-2 hover:underline`}
              >
                {c.label}
              </a>
            </React.Fragment>
          ))}
        </p>
      ) : null}
      {item.fullReleaseUrl ? (
        <div className="pt-2">
          <a
            href={item.fullReleaseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${fullReleaseCtaClass} text-xs`}
          >
            Read more
            <ArrowRight
              className="h-3.5 w-3.5 shrink-0 text-current"
              aria-hidden
            />
          </a>
        </div>
      ) : null}
    </div>
  </article>
);

const InTheNewsPage = () => {
  const [activeTag, setActiveTag] = useState(IN_THE_NEWS_FILTER_TAGS[0]);

  const filteredItems = useMemo(() => {
    if (activeTag === "All") return IN_THE_NEWS_ITEMS;

    return IN_THE_NEWS_ITEMS.filter(
      (item) =>
        Array.isArray(item.filters) &&
        item.filters.some((f) => f === activeTag),
    );
  }, [activeTag]);

  const [featured, ...rest] = filteredItems;

  return (
    <PublicLayout>
      <div className="bg-[#1A1A1A] text-white pt-[72px] overflow-x-hidden">
        <section className="bg-[rgb(77_148_255/var(--tw-bg-opacity))]">
          <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-10 sm:py-24">
            <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-start">
              <div className="text-left text-white md:max-w-[40%]">
                <p className="m-0 text-base font-semibold uppercase tracking-wide text-white/90">
                  Press & media
                </p>
                <h1 className="mt-4 mb-4 text-4xl sm:text-5xl font-bold leading-tight">
                  In the news
                </h1>
                <p className="m-0 max-w-3xl whitespace-nowrap text-white/95 text-lg sm:text-xl">
                  Coverage, announcements and press releases from unzippd.
                </p>
              </div>
              <div className="pointer-events-none absolute right-0 top-0 hidden md:block">
                <img
                  src={heroImage}
                  alt=""
                  className="h-28 w-28 sm:h-36 sm:w-36 object-contain"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#1A1A1A] text-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {IN_THE_NEWS_FILTER_TAGS.map((tag) => {
                const selected = tag === activeTag;

                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setActiveTag(tag)}
                    className={`cursor-pointer rounded-full px-4 py-2 text-sm font-semibold transition-colors border ${
                      selected
                        ? "bg-[rgb(77_148_255/var(--tw-bg-opacity))] text-white border-transparent"
                        : "bg-transparent text-white/90 border-white/25 hover:border-white/45 hover:bg-white/5"
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-[#1A1A1A] text-white min-h-[40vh]">
          {!featured ? (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 text-center text-gray-400">
              Nothing in this category yet.
            </div>
          ) : (
            <>
              <div className="px-4 sm:px-6 lg:px-8 pt-0 pb-6 sm:pb-8 md:hidden">
                <SecondaryCard item={featured} />
              </div>

              <FeaturedCard item={featured} />

              {rest.length > 0 ? (
                <div className="mx-auto max-w-7xl min-w-0 px-4 sm:px-6 lg:px-8 pt-0 pb-12 sm:pb-16 bg-[#1A1A1A] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 lg:gap-x-8 lg:gap-y-12 items-start">
                  {rest.map((item, idx) => (
                    <SecondaryCard key={`${item.title}-${idx}`} item={item} />
                  ))}
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>
    </PublicLayout>
  );
};

export default InTheNewsPage;
